import { useContext } from "react";
import type { ComponentProps, MouseEvent, MouseEventHandler, ReactNode } from "react";
import { Row, Button, Tooltip, useBreakpoints } from "@narmi/design_system";
import ContextForm, { FormContext } from "../ContextForm";
import BackCancelButton from "../BackCancelButton";
import StickyProgressButtons from "../StickyProgressButtons";

export type ProgressButtonsProps = {
  onBack?: MouseEventHandler<HTMLButtonElement> | null | false;
  backLabel?: string;
  // ContextForm supplies a completion callback and event; standalone buttons supply an event.
  onNext?:
    | MouseEventHandler<HTMLButtonElement>
    | ((complete: (errors?: unknown) => void, event: MouseEvent<HTMLButtonElement>) => void);
  nextLabel?: ReactNode;
  kind?: ComponentProps<typeof Button>["kind"];
  dangerouslyDisableShowLoading?: boolean;
  disabled?: boolean;
  disableNext?: boolean;
  hidePrev?: boolean;
  backTooltip?: string | null | false;
  nextTooltip?: string | null | false;
  shouldHideInlineToasts?: boolean;
  isNextLoading?: boolean;
  nextTestId?: string;
  enableStickyMobile?: boolean;
};

const ProgressButtons = ({
  onBack,
  backLabel,
  onNext,
  nextLabel = "Next",
  kind = "primary",
  // Setting this property to true will disable the multiple click prevention that the loading state provides.
  dangerouslyDisableShowLoading,
  disabled,
  disableNext = false,
  hidePrev = false,
  backTooltip,
  nextTooltip,
  shouldHideInlineToasts = false,
  isNextLoading = false,
  nextTestId = "next",
  // Enable sticky mobile footer buttons (opt-in for AO pages)
  enableStickyMobile = false,
}: ProgressButtonsProps) => {
  const formContext = useContext(FormContext);
  const { s: isDesktop } = useBreakpoints();

  // Use sticky footer on mobile if enabled
  if (!isDesktop && enableStickyMobile) {
    return (
      <StickyProgressButtons
        onBack={onBack}
        backLabel={backLabel}
        onNext={onNext}
        nextLabel={nextLabel}
        kind={kind}
        dangerouslyDisableShowLoading={dangerouslyDisableShowLoading}
        disabled={disabled}
        disableNext={disableNext}
        hidePrev={hidePrev}
        shouldHideInlineToasts={shouldHideInlineToasts}
        isNextLoading={isNextLoading}
        nextTestId={nextTestId}
        backTooltip={backTooltip}
        nextTooltip={nextTooltip}
      />
    );
  }

  const getNextButton = () => {
    const next = (
      <Button
        kind={kind}
        // Existing callers pass JSX labels, which Button renders despite its string-only type.
        label={nextLabel as string}
        testId={nextTestId}
        disabled={disableNext}
        onClick={formContext ? () => {} : (onNext as MouseEventHandler<HTMLButtonElement>)}
        isLoading={isNextLoading}
      />
    );
    return nextTooltip ? <Tooltip text={nextTooltip}>{next}</Tooltip> : next;
  };
  const getBackButton = () => {
    if (hidePrev) return;
    const back = (
      <BackCancelButton
        // Preserve legacy falsy handlers without coercing them at runtime.
        onBack={onBack as MouseEventHandler<HTMLButtonElement>}
        backLabel={backLabel}
        disabled={disabled}
      />
    );
    return backTooltip ? <Tooltip text={backTooltip}>{back}</Tooltip> : back;
  };
  return (
    <div className="progress-buttons">
      <Row justifyContent="space-between" alignItems="center">
        <Row.Item>{getBackButton()}</Row.Item>
        <Row.Item shrink>
          {formContext ? (
            <ContextForm.Action
              onSubmit={onNext}
              dangerouslyDisableShowLoading={dangerouslyDisableShowLoading}
              shouldHideInlineToasts={shouldHideInlineToasts}
            >
              {getNextButton()}
            </ContextForm.Action>
          ) : (
            getNextButton()
          )}
        </Row.Item>
      </Row>
    </div>
  );
};

export default ProgressButtons;
